/**
 * The user block at the foot of the sidebar (#2476 S9): avatar initial, name
 * and role. It opens the account menu the top bar used to carry — profile,
 * language, sign-out. Since #2511 it is shadcn's sidebar user block (a large
 * menu button with a chevron, the menu opening to the side), and it shrinks
 * to the avatar when the sidebar collapses to icons.
 *
 * Open/close, outside-click dismissal, Escape handling and focus management
 * are owned by the shadcn/Base-UI DropdownMenu primitive (#1291).
 */
import { Link } from 'react-router-dom';
import { Check, ChevronsUpDown, LogOut, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@resource-kit/ui/react';
import { sidebarMenuButtonVariants, useSidebar } from '@/components/ui/sidebar';
import { useAuth } from '@hooks/useAuth';
import { useLocale, SUPPORTED_LOCALES } from '@hooks/useLocale';
import { ROLE_LABELS } from '@/permissions';

/** The user's initial in a square, shared by the trigger and the menu's identity row. */
function UserAvatar({ initial }: { initial: string }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-accent text-xs font-semibold">
      {initial}
    </span>
  );
}

export default function SidebarUserMenu() {
  const { user, signOut } = useAuth();
  const { locale, setLocale } = useLocale();
  const { t } = useTranslation();
  const { isMobile } = useSidebar();

  const displayName = user?.name || user?.email || '';
  const initial = (displayName || 'U').charAt(0).toUpperCase();
  const roleKey = ROLE_LABELS[user?.role ?? ''] ?? 'role.viewer';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-haspopup="true"
        data-testid="user-menu-trigger"
        className={cn(
          sidebarMenuButtonVariants({ size: 'lg' }),
          'cursor-pointer data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground',
        )}
      >
        <UserAvatar initial={initial} />
        <span className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate font-medium">{displayName}</span>
          <span className="truncate text-xs text-muted-foreground">{t(roleKey)}</span>
        </span>
        <ChevronsUpDown className="ml-auto size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56"
        positionerProps={{ side: isMobile ? 'bottom' : 'right', align: 'end', sideOffset: 4 }}
      >
        {/* User identity summary, shaped like shadcn NavUser's label row
            (#2514): avatar, name, email. Non-interactive. */}
        {user && (
          <>
            <div
              data-testid="user-menu-identity"
              className="flex items-center gap-2 px-1 py-1.5 text-left text-sm"
            >
              <UserAvatar initial={initial} />
              <div className="grid flex-1 leading-tight">
                <span className="truncate font-medium">{user.name || t('ui.none')}</span>
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              </div>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem render={<Link to="/profile" />} className="no-underline">
          <User className="w-4 h-4 text-muted-foreground" />
          {t('nav.profile')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {SUPPORTED_LOCALES.map(({ code, label }) => {
          const current = code === locale;
          return (
            <DropdownMenuItem
              key={code}
              role="menuitemradio"
              aria-checked={current}
              onClick={() => setLocale(code)}
              className={cn(current && 'text-primary font-medium')}
            >
              <Check className={cn('w-4 h-4', current ? 'opacity-100' : 'opacity-0')} />
              {label}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          data-testid="logout-button"
          onClick={() => {
            signOut().catch(() => {});
          }}
        >
          <LogOut className="w-4 h-4 text-muted-foreground" />
          {t('nav.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
